import { FC, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Formik } from "formik";
import { t } from "i18next";

import { toasts } from "@/shared/utils/toasts";
import { InputFile } from "@/shared/components/atoms/InputFile";
import { InputFileLarge } from "@/shared/components/atoms/InputFileLarge";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";

import { branchValidationSchema } from "@/shared/schemas/settings.schema";

// API
import { useUpdateBrandMutation } from "@/app/brand/brand.api";
import { useGetBranchQuery } from "@/app/branchs/branchs.api";
import { DOMAIN } from "@/app/app.api";

export const Customization: FC<any> = () => {
  const { id } = useParams();
  const getBranchQuery = useGetBranchQuery({
    branchId: id,
  });

  const [updateBrand, updateBrandResult] = useUpdateBrandMutation();

  const brandInitialValues = useMemo(
    () => ({
      logo:
        typeof getBranchQuery.data?.logo === "string" &&
        getBranchQuery.data?.logo !== ""
          ? `${DOMAIN}/uploads/branchs/${getBranchQuery.data?.logo}`
          : "",
      large_logo:
        typeof getBranchQuery.data?.large_logo === "string" &&
        getBranchQuery.data?.large_logo !== ""
          ? `${DOMAIN}/uploads/branch_large_logo/${getBranchQuery.data?.large_logo}`
          : "",
      firstColor: getBranchQuery.data?.first_color || "",
      secoundColor: getBranchQuery.data?.secound_color || "",
    }),
    [getBranchQuery.data]
  );

  const handleUpdateBrand = async (values: any) => {
    try {
      const formToSend = new FormData();

      if (values.logo instanceof File) {
        formToSend.append("logo", values.logo);
      }

      if (values.large_logo instanceof File) {
        formToSend.append("large_logo", values.large_logo);
      }
      formToSend.append("first_color", values.firstColor);
      formToSend.append("secound_color", values.secoundColor);
      formToSend.append("branch_id", id);

      await updateBrand({ gymData: formToSend }).unwrap();
      toasts.success(
        "Actualización correcta",
        "Datos actualizados correctamente"
      );
      getBranchQuery.refetch();
    } catch (error) {
      toasts.danger(
        "Error al actualizar",
        "Ha ocurrido un error al actualizar los datos"
      );
      throw error;
    }
  };

  return (
    <div className="flex flex-col w-full h-full">
      <div className="w-full mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-lg font-semibold text-gray-900">
            {t("share.views.customization_logo_and_corporate_colors")}
          </h1>
          <div className="border-b border-gray-300 mt-4"></div>
        </div>

        <Formik
          enableReinitialize
          initialValues={brandInitialValues}
          validationSchema={branchValidationSchema}
          onSubmit={async (values, formikHelpers) => {
            try {
              await handleUpdateBrand(values);
              formikHelpers.resetForm();
            } catch (error) {
              console.log("Error on handleUpdateBrand", error);
            }
          }}
        >
          {({ handleChange, handleSubmit, errors, values, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              <div className="space-y-8 max-w-xl mx-auto">
                {/* Logos Section */}
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="flex-1">
                    <InputFile
                      name="logo"
                      id="logo"
                      label={t("branch.views.logo")}
                      spanButton={t("branch.views.upload_image")}
                      description={t("branch.views.description_upload_image")}
                      selectedImage={values.logo}
                      onFileChange={(event) => {
                        setFieldValue("logo", event);
                      }}
                      onRemoveImage={() => setFieldValue("logo", null)}
                      tittleDelete={t("general.delete_image")}
                    />
                    {errors.logo ? (
                      <span className="text-xs text-red-500">
                        {errors.logo}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex-1">
                    <InputFileLarge
                      name="large_logo"
                      id="large_logo"
                      label={t("branch.views.large_logo")}
                      spanButton={t("branch.views.upload_image")}
                      description={t("branch.views.description_upload_image")}
                      selectedImage={values.large_logo}
                      onFileChange={(event) => {
                        setFieldValue("large_logo", event);
                      }}
                      onRemoveImage={() => setFieldValue("large_logo", null)}
                      tittleDelete={t("general.delete_image")}
                    />
                    {errors.large_logo ? (
                      <span className="text-xs text-red-500">
                        {errors.large_logo}
                      </span>
                    ) : null}
                  </div>
                </div>

                {/* Colors Section */}
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="flex-1">
                    <InputWithCornerHint
                      label={t("branch.views.color_primary")}
                      type="color"
                      name="firstColor"
                      value={values.firstColor}
                      onChange={handleChange}
                      error={!!errors.firstColor}
                      textError={errors.firstColor}
                    />
                  </div>
                  <div className="flex-1">
                    <InputWithCornerHint
                      label={t("branch.views.color_secondary")}
                      type="color"
                      name="secoundColor"
                      value={values.secoundColor}
                      onChange={handleChange}
                      error={!!errors.secoundColor}
                      textError={errors.secoundColor}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-center pt-4">
                  <button
                    type="submit"
                    disabled={
                      updateBrandResult.isLoading ||
                      Object.keys(errors).length > 0
                    }
                    className="inline-flex gap-2 justify-center items-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 disabled:opacity-75 disabled:cursor-not-allowed"
                  >
                    {updateBrandResult.isLoading && (
                      <div
                        className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white"
                        role="status"
                      ></div>
                    )}
                    {t("general.save")}
                  </button>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default Customization;
